import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { createEnquiry } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import OrnamentalDivider from "@/components/OrnamentalDivider";

export const EnquiryForm = ({ tourType = "corporate", heading, orgLabel }) => {
  const [form, setForm] = useState({
    contact_name: "", organisation: "", email: "", phone: "",
    group_size: 15, preferred_date: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.organisation || !form.email) {
      toast.error("Name, organisation and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await createEnquiry({
        tour_type: tourType,
        contact_name: form.contact_name,
        organisation: form.organisation,
        email: form.email,
        phone: form.phone || null,
        group_size: Number(form.group_size),
        preferred_date: form.preferred_date || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success("Enquiry sent — Martin will be in touch");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="card-warm rounded-2xl p-10 text-center" data-testid="enquiry-confirmed">
        <div className="serif text-3xl text-[#6E2638]">Enquiry received</div>
        <OrnamentalDivider />
        <p className="text-[#1A1817]/80">
          Thanks — Martin has been notified. He'll reply within 2 business days.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-warm rounded-2xl p-8" data-testid={`enquiry-form-${tourType}`}>
      <h3 className="serif text-2xl text-[#6E2638]">
        {heading || `Enquire about a ${tourType} tour`}
      </h3>
      <OrnamentalDivider />
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <Label>Contact name</Label>
          <Input value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)}
            data-testid="enq-name" required />
        </div>
        <div>
          <Label>{orgLabel || (tourType === "school" ? "School" : "Organisation")}</Label>
          <Input value={form.organisation} onChange={(e) => update("organisation", e.target.value)}
            data-testid="enq-org" required />
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
            data-testid="enq-email" required />
        </div>
        <div>
          <Label>Phone (optional)</Label>
          <Input value={form.phone} onChange={(e) => update("phone", e.target.value)}
            data-testid="enq-phone" />
        </div>
        <div>
          <Label>Group size</Label>
          <Input type="number" min={1} value={form.group_size}
            onChange={(e) => update("group_size", e.target.value)} data-testid="enq-size" />
        </div>
        <div>
          <Label>Preferred date</Label>
          <Input type="date" value={form.preferred_date}
            onChange={(e) => update("preferred_date", e.target.value)} data-testid="enq-date" />
        </div>
      </div>
      <div className="mt-5">
        <Label>Message</Label>
        <Textarea rows={4} value={form.message} onChange={(e) => update("message", e.target.value)}
          data-testid="enq-message" placeholder="Tell Martin about your group, focus, and any constraints…" />
      </div>
      <button disabled={submitting} className="btn-primary mt-8 w-full justify-center" data-testid={`submit-enquiry-${tourType}`}>
        {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send enquiry"}
      </button>
    </form>
  );
};

export default EnquiryForm;
